import { useEffect, useState } from 'react'
import { VisitLog } from '@/types'
import { usePlanningsEventsManager } from '@/hooks'

type UseOtherRoundsPhotosProps = {
  visitLog: VisitLog
  enabled?: boolean
}

/**
 * Hook to check if other rounds related to the same research area have photos
 * @param visitLog The current visit log
 * @param enabled Whether the hook should be enabled
 * @returns An object with a boolean indicating if other rounds have photos
 */
export const useOtherRoundsPhotos = ({
  visitLog,
  enabled = true,
}: UseOtherRoundsPhotosProps) => {
  const [otherRoundsHavePhotos, setOtherRoundsHavePhotos] = useState(false)

  const researchAreaId = visitLog.research_visit_event?.research_area.id

  const { data: eventsData, isLoading } = usePlanningsEventsManager(
    {
      researchArea: researchAreaId?.toString(),
      limit: 100,
    },
    enabled && !!researchAreaId
  )

  useEffect(() => {
    if (!eventsData || !visitLog.research_visit_event?.field_report) {
      return
    }

    const otherEvents = eventsData.results.filter(
      (event) => event.id !== visitLog.research_visit_event?.id
    )

    // Check if any of the other events have field reports with photos
    // Note: field_report might not be directly available in ManagerResearchVisitEvent
    // We need to check if the event has a field report with photos
    const hasPhotos = otherEvents.some(
      (event) =>
        event.field_report?.photos && event.field_report.photos.length > 0
    )

    setOtherRoundsHavePhotos(hasPhotos)
  }, [eventsData, visitLog, researchAreaId])

  return {
    otherRoundsHavePhotos,
    isLoading,
  }
}
